import type { PublicAgent } from '@magic/schemas/agents';
import type { Role } from '@magic/schemas/messages';
import { Text } from '@resultadosdigitais/tangram-components';
import remarkGfm from 'remark-gfm';
import styled, { css } from 'styled-components';

import { WithChildren } from '@exact-magic/react-utils';
import { Avatar } from './avatar';
import * as components from './components';
import { Loader } from './loader';
import { MemoizedReactMarkdown } from './markdown';
import { Timestamp } from './timestamp';

interface MessageProps {
  children: string;
  author: string;
  role: Role;
  timestamp?: Date;
}

const StyledAvatarWrapper = styled.div`
  display: flex;
  gap: var(--size-spacing-03);
`;

const StyledMessageHeader = styled.header`
  display: flex;
  gap: var(--size-spacing-03);
  justify-content: space-between;
`;

const StyledMessageContent = styled.div<{ role?: Role }>`
  border-radius: var(--border-radius-rounded);
  padding: var(--size-spacing-04);
  background-color: var(--neutral-surface-low-emphasis);
  overflow-wrap: break-word;
  word-break: break-word;
  color: inherit;

  ${({ role }) =>
    role === 'user' &&
    css`
      color: var(--neutral-text-inverse);
      background-color: var(--neutral-surface-inverse);
    `}
`;

const StyledMessage = styled.div<{ $loading?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--size-spacing-02);
  width: fit-content;
  max-width: 90%;

  ${({ $loading }) =>
    $loading &&
    css`
      max-width: fit-content;
    `}
`;

const StyledHumanMessageWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Author = ({ children }: WithChildren) => (
  <Text as="span" token={Text.tokens.TEXT_XS_BOLD}>
    {children}
  </Text>
);

const Message = ({ children, role, author, timestamp }: MessageProps) => {
  return (
    <StyledMessage>
      <StyledMessageHeader>
        <Author>{author}</Author>
        <Text
          as="span"
          token={Text.tokens.TEXT_XS_REGULAR}
          textColor="--neutral-text-low-emphasis"
        >
          <Timestamp value={timestamp} />
        </Text>
      </StyledMessageHeader>

      <StyledMessageContent
        className={role === 'user' ? 'prose prose-invert' : 'prose'}
        role={role}
      >
        <MemoizedReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {children}
        </MemoizedReactMarkdown>
      </StyledMessageContent>
    </StyledMessage>
  );
};

const AvatarWrapper = ({
  agent,
  children,
}: WithChildren<{ agent: PublicAgent }>) => (
  <StyledAvatarWrapper>
    <div>
      <Avatar size="sm" agent={agent} />
    </div>
    {children}
  </StyledAvatarWrapper>
);

interface TypingProps {
  agent: PublicAgent;
}

export const Typing = ({ agent }: TypingProps) => (
  <AvatarWrapper agent={agent}>
    <StyledMessage $loading>
      <StyledMessageContent>
        <Loader />
      </StyledMessageContent>
    </StyledMessage>
  </AvatarWrapper>
);

interface AssistantMessageProps
  extends Pick<MessageProps, 'children' | 'timestamp'> {
  agent: PublicAgent;
}

export const AssistantMessage = ({
  agent,
  timestamp,
  children,
}: AssistantMessageProps) => {
  return (
    <AvatarWrapper agent={agent}>
      <Message
        author={agent.name || ''}
        timestamp={timestamp}
        role={'assistant'}
      >
        {children}
      </Message>
    </AvatarWrapper>
  );
};

export const UserMessage = ({
  timestamp,
  children,
}: Pick<MessageProps, 'children' | 'timestamp'>) => {
  return (
    <StyledHumanMessageWrapper>
      <Message author="Você" timestamp={timestamp} role="user">
        {children}
      </Message>
    </StyledHumanMessageWrapper>
  );
};
